import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '@/lib/supabase';
import type {
  User,
  AuthSession,
  AuthContextType,
  LoginCredentials,
  RegisterCredentials,
  AdminRole,
} from '@/types';

// Allowed values for onboarding fields (must match NewRegisterPage constants)
const VALID_COUNTRIES = new Set([
  'MA',
  'FR',
  'ES',
  'BE',
  'DE',
  'CA',
  'CH',
  'GB',
  'IT',
  'PT',
  'US',
  'NL',
  'TN',
  'DZ',
  'SN',
  'CI',
  'LU',
  'AT',
  'IE',
  'SE',
]);
const VALID_LIFE_SITUATIONS = new Set([
  'student',
  'alternant',
  'young_active',
  'employee',
  'entrepreneur',
  'no_income',
  'other',
]);
const VALID_MANAGEMENT_STYLES = new Set(['solo', 'couple', 'family']);
const VALID_GENDERS = new Set(['male', 'female', 'prefer_not_to_say']);
const VALID_CHALLENGES = new Set([
  'track_expenses',
  'save_regularly',
  'where_money_goes',
  'manage_couple',
  'manage_debts',
  'track_investments',
  'other',
]);
const VALID_EXPECTATIONS = new Set([
  'budget_clarity',
  'prepare_project',
  'safety_savings',
  'household_finance',
  'track_patrimoine',
  'money_basics',
]);
const VALID_DISCOVERY_SOURCES = new Set([
  'facebook',
  'instagram',
  'x',
  'youtube',
  'tiktok',
  'linkedin',
  'google',
  'friends',
  'other',
]);

function sanitizeString(val: unknown, maxLen = 200): string | null {
  if (typeof val !== 'string') return null;
  const trimmed = val.trim().slice(0, maxLen);
  return trimmed || null;
}

function validateOnboardingData(raw: unknown) {
  if (!raw || typeof raw !== 'object') return null;
  const d = raw as Record<string, unknown>;

  const firstName = sanitizeString(d.firstName, 100);
  if (!firstName) return null;

  const age = (() => {
    if (typeof d.dateOfBirth === 'string' && d.dateOfBirth) {
      const dob = new Date(d.dateOfBirth);
      const today = new Date();
      let a = today.getFullYear() - dob.getFullYear();
      const m = today.getMonth() - dob.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) a--;
      return a;
    }
    if (typeof d.age === 'string') return parseInt(d.age, 10);
    if (typeof d.age === 'number') return d.age;
    return NaN;
  })();
  if (isNaN(age) || age < 13 || age > 120) return null;

  const country = sanitizeString(d.country, 5);
  if (!country || !VALID_COUNTRIES.has(country)) return null;

  const gender = sanitizeString(d.gender, 30);
  if (gender && !VALID_GENDERS.has(gender)) return null;

  const lifeSituation = sanitizeString(d.lifeSituation, 50);
  if (lifeSituation && !VALID_LIFE_SITUATIONS.has(lifeSituation)) return null;

  const managementStyle = sanitizeString(d.managementStyle, 50);
  if (managementStyle && !VALID_MANAGEMENT_STYLES.has(managementStyle)) return null;

  const challenges = Array.isArray(d.challenges)
    ? (d.challenges as unknown[]).filter(
        (c): c is string => typeof c === 'string' && VALID_CHALLENGES.has(c)
      )
    : [];

  const expectations = Array.isArray(d.expectations)
    ? (d.expectations as unknown[]).filter(
        (e): e is string => typeof e === 'string' && VALID_EXPECTATIONS.has(e)
      )
    : [];

  const discoverySource = sanitizeString(d.discoverySource, 50);
  if (discoverySource && !VALID_DISCOVERY_SOURCES.has(discoverySource)) return null;

  const dateOfBirth =
    typeof d.dateOfBirth === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d.dateOfBirth)
      ? d.dateOfBirth
      : null;

  return {
    first_name: firstName,
    age,
    date_of_birth: dateOfBirth,
    gender,
    country_code: country,
    life_situation: lifeSituation,
    life_situation_other: lifeSituation === 'other' ? sanitizeString(d.lifeSituationOther) : null,
    management_style: managementStyle,
    challenges,
    challenge_other: challenges.includes('other') ? sanitizeString(d.challengeOther) : null,
    expectations,
    discovery_source: discoverySource,
    discovery_other: discoverySource === 'other' ? sanitizeString(d.discoveryOther) : null,
    referral_code: sanitizeString(d.referralCode, 50),
  };
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<AuthSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [onboardingComplete, setOnboardingComplete] = useState<boolean | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminRole, setAdminRole] = useState<AdminRole>(null);
  const [mfaPending, setMfaPending] = useState(false);

  const checkAdminStatus = async (userId: string) => {
    const cacheKey = 'clevia_is_admin';
    const cacheRoleKey = 'clevia_admin_role';
    const cached = localStorage.getItem(cacheKey);
    if (cached === userId) {
      setIsAdmin(true);
      setAdminRole((localStorage.getItem(cacheRoleKey) as AdminRole) || 'admin');
      return;
    }
    try {
      const { data: row } = await supabase
        .from('admin_users')
        .select('role')
        .eq('user_id', userId)
        .maybeSingle();
      const isAdm = !!row;
      setIsAdmin(isAdm);
      setAdminRole(isAdm ? (row.role as AdminRole) : null);
      if (isAdm) {
        localStorage.setItem(cacheKey, userId);
        localStorage.setItem(cacheRoleKey, row.role);
      } else {
        localStorage.removeItem(cacheKey);
        localStorage.removeItem(cacheRoleKey);
      }
    } catch {
      setIsAdmin(false);
      setAdminRole(null);
    }
  };

  const checkOnboarding = async (userId: string) => {
    // Use localStorage cache for instant reload
    const cacheKey = 'clevia_onboarding_done';
    const cached = localStorage.getItem(cacheKey);

    if (cached === userId) {
      setOnboardingComplete(true);
      return;
    }

    try {
      const query = supabase
        .from('user_onboarding')
        .select('user_id')
        .eq('user_id', userId)
        .maybeSingle();
      const result = await Promise.race([
        query,
        new Promise<never>((_, reject) => setTimeout(() => reject(new Error('timeout')), 8000)),
      ]);
      const complete = !!result.data;
      setOnboardingComplete(complete);
      if (complete) {
        localStorage.setItem(cacheKey, userId);
      } else {
        localStorage.removeItem(cacheKey);
      }
    } catch {
      setOnboardingComplete(false);
    }
  };

  const buildUserObj = (supaUser: {
    id: string;
    email?: string | null;
    user_metadata?: Record<string, unknown>;
    created_at: string;
    updated_at?: string;
  }) => ({
    id: supaUser.id,
    email: supaUser.email!,
    name: supaUser.user_metadata?.name as string | undefined,
    createdAt: supaUser.created_at,
    updatedAt: supaUser.updated_at || supaUser.created_at,
  });

  const buildSessionObj = (s: {
    user: {
      id: string;
      email?: string | null;
      user_metadata?: Record<string, unknown>;
      created_at: string;
      updated_at?: string;
    };
    access_token: string;
    refresh_token: string;
    expires_at?: number;
  }) => ({
    user: buildUserObj(s.user),
    accessToken: s.access_token,
    refreshToken: s.refresh_token,
    expiresAt: s.expires_at || 0,
  });

  useEffect(() => {
    // Use only onAuthStateChange — handles INITIAL_SESSION (reload) + SIGNED_IN (login)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        setUser(buildUserObj(session.user));
        setSession(buildSessionObj(session));

        // For SIGNED_IN events, check MFA before allowing navigation
        if (_event === 'SIGNED_IN') {
          (async () => {
            try {
              const { data: factors } = await supabase.auth.mfa.listFactors();
              const verified = factors?.totp.filter((f) => f.status === 'verified') || [];
              if (verified.length > 0) {
                const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
                if (aal?.currentLevel === 'aal1' && aal?.nextLevel === 'aal2') {
                  setMfaPending(true);
                }
              }
            } catch {
              // MFA check failed — don't block login
            } finally {
              setIsLoading(false);
            }
          })();
        } else {
          setIsLoading(false);
        }

        // Save pending onboarding data from localStorage (only on actual sign-in with confirmed email)
        if (_event === 'SIGNED_IN' && session.user.email_confirmed_at) {
          const pendingData = localStorage.getItem('clevia_pending_onboarding');
          if (pendingData) {
            (async () => {
              try {
                const parsed = JSON.parse(pendingData);
                const validated = validateOnboardingData(parsed);
                if (validated) {
                  await supabase.from('user_onboarding').insert({
                    user_id: session.user.id,
                    ...validated,
                    onboarding_completed_at: new Date().toISOString(),
                  });
                } else {
                  console.error('Invalid onboarding data in localStorage — skipped insert');
                }
              } catch (e) {
                console.error('Error saving pending onboarding data:', e);
              } finally {
                localStorage.removeItem('clevia_pending_onboarding');
              }
            })();
          }
        }

        // Run onboarding + admin checks async (don't block loading)
        checkOnboarding(session.user.id);
        checkAdminStatus(session.user.id);
      } else {
        setUser(null);
        setSession(null);
        setOnboardingComplete(null);
        setIsAdmin(false);
        setIsLoading(false);
      }
    });

    // Safety timeout: if auth takes too long, stop loading to prevent infinite spinner
    const safetyTimeout = setTimeout(() => {
      setIsLoading((prev) => {
        if (prev) console.warn('Auth loading timeout — forcing isLoading=false');
        return false;
      });
    }, 10000);

    // Refresh session when tab regains focus (handles expired tokens after background)
    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;

      // Try to refresh the session proactively when user comes back
      const { data, error } = await supabase.auth.refreshSession();
      if (error || !data.session) {
        // Refresh token is dead — clean up locally so user can re-login
        supabase.auth.signOut({ scope: 'local' });
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Also refresh periodically while the tab is open (every 10 min)
    // This prevents token expiry when the user keeps the tab open but idle
    const refreshInterval = setInterval(
      async () => {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (session) {
          const expiresAt = session.expires_at ?? 0;
          const now = Math.floor(Date.now() / 1000);
          // Refresh if token expires within 5 minutes
          if (expiresAt - now < 300) {
            const { error } = await supabase.auth.refreshSession();
            if (error) {
              supabase.auth.signOut({ scope: 'local' });
            }
          }
        }
      },
      10 * 60 * 1000
    );

    return () => {
      subscription.unsubscribe();
      clearTimeout(safetyTimeout);
      clearInterval(refreshInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const login = async ({ email, password }: LoginCredentials) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) throw error;

    if (data.user) {
      setUser({
        id: data.user.id,
        email: data.user.email!,
        name: data.user.user_metadata?.name,
        createdAt: data.user.created_at,
        updatedAt: data.user.updated_at || data.user.created_at,
      });
    }
  };

  const register = async ({ email, password, name }: RegisterCredentials) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          name,
        },
      },
    });

    if (error) throw error;

    if (data.user) {
      setUser({
        id: data.user.id,
        email: data.user.email!,
        name: data.user.user_metadata?.name,
        createdAt: data.user.created_at,
        updatedAt: data.user.updated_at || data.user.created_at,
      });
    }
  };

  const logout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setUser(null);
    setSession(null);
    setMfaPending(false);
    localStorage.removeItem('clevia_onboarding_done');
    localStorage.removeItem('clevia_is_admin');
    localStorage.removeItem('clevia_admin_role');
    sessionStorage.removeItem('clevia_admin_chose');
    setIsAdmin(false);
    setAdminRole(null);
  };

  const refreshSession = async () => {
    const { data, error } = await supabase.auth.refreshSession();
    if (error) throw error;

    if (data.session) {
      setSession({
        user: {
          id: data.session.user.id,
          email: data.session.user.email!,
          name: data.session.user.user_metadata?.name,
          createdAt: data.session.user.created_at,
          updatedAt: data.session.user.updated_at || data.session.user.created_at,
        },
        accessToken: data.session.access_token,
        refreshToken: data.session.refresh_token,
        expiresAt: data.session.expires_at || 0,
      });
    }
  };

  const updateProfile = async (data: { name?: string; phone?: string; address?: string }) => {
    const { error } = await supabase.auth.updateUser({
      data: {
        name: data.name,
        phone: data.phone,
        address: data.address,
      },
    });

    if (error) throw error;

    // Update local user state
    if (user) {
      setUser({
        ...user,
        name: data.name || user.name,
        updatedAt: new Date().toISOString(),
      });
    }
  };

  const value: AuthContextType = {
    user,
    session,
    isLoading,
    isAuthenticated: !!user,
    isAdmin,
    adminRole,
    onboardingComplete,
    mfaPending,
    completeMfa: () => setMfaPending(false),
    refreshOnboarding: async () => {
      if (user) await checkOnboarding(user.id);
    },
    login,
    register,
    logout,
    refreshSession,
    updateProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
